import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Surface, Badge, Button, Modal, Input, Select, NewButton } from "@/components/ui-kit";
import VehicleSearchSelect from "@/components/VehicleSearchSelect";
import { X } from "@phosphor-icons/react";

const empty = { vehicle_id: "", name: "", interval_km: 10000, interval_days: 0, last_service_km: 0 };

export default function PMSchedules() {
    const [list, setList] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);

    // Filters
    const [filterVehicle, setFilterVehicle] = useState("");
    const [filterStatus, setFilterStatus] = useState(""); // ""|due|near|ok
    const [filterHorizon, setFilterHorizon] = useState(""); // ""|15|30 — km-or-days "coming up within N days/horizon"

    const load = () => api.get("/pm-schedules").then((r) => setList(r.data));
    useEffect(() => {
        load();
        api.get("/vehicles").then((r) => setVehicles(r.data));
    }, []);

    const save = async () => {
        try {
            const payload = { ...form, interval_km: Number(form.interval_km), interval_days: Number(form.interval_days), last_service_km: Number(form.last_service_km) };
            if (editing) await api.put(`/pm-schedules/${editing}`, payload);
            else await api.post("/pm-schedules", payload);
            toast.success("Saved"); setOpen(false); setEditing(null); setForm(empty); load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const trigger = async (pm) => {
        try {
            await api.post(`/pm-schedules/${pm.id}/trigger`);
            toast.success("Work order generated");
            load();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    };

    const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/pm-schedules/${id}`); load(); };
    const edit = (p) => { setForm({ ...empty, ...p }); setEditing(p.id); setOpen(true); };

    const vehMap = Object.fromEntries(vehicles.map((v) => [v.id, v]));

    // Compute schedule status + days/km to due, then apply filters
    const decorated = useMemo(() => {
        return list.map((p) => {
            const veh = vehMap[p.vehicle_id];
            const km_since = (veh?.odometer || 0) - (p.last_service_km || 0);
            const km_to_due = (p.interval_km > 0) ? (p.interval_km - km_since) : null;
            const due = p.interval_km > 0 && km_since >= p.interval_km;
            const near = p.interval_km > 0 && km_since >= p.interval_km * 0.9 && !due;
            const status = due ? "due" : near ? "near" : "ok";

            // Days-to-due — only when an interval_days is configured & we have a last-serviced timestamp
            let days_to_due = null;
            if (p.interval_days > 0 && p.last_service_at) {
                const nextDue = new Date(new Date(p.last_service_at).getTime() + p.interval_days * 86400000);
                days_to_due = Math.ceil((nextDue - new Date()) / 86400000);
            }
            return { ...p, _veh: veh, _kmSince: km_since, _kmToDue: km_to_due, _daysToDue: days_to_due, _status: status };
        });
    }, [list, vehMap]);

    const filtered = useMemo(() => {
        return decorated.filter((p) => {
            if (filterVehicle && p.vehicle_id !== filterVehicle) return false;
            if (filterStatus && p._status !== filterStatus) return false;
            if (filterHorizon) {
                const horizonDays = Number(filterHorizon);
                // Match if either:
                //  (a) days-to-due fits inside the horizon (incl. overdue), OR
                //  (b) km-to-due fits inside an estimated daily-avg km horizon (50km/day fallback)
                const fitDays = p._daysToDue != null && p._daysToDue <= horizonDays;
                const dailyKm = 50; // conservative fleet-wide average; tunable later
                const fitKm = p._kmToDue != null && p._kmToDue <= horizonDays * dailyKm;
                if (!fitDays && !fitKm) return false;
            }
            return true;
        });
    }, [decorated, filterVehicle, filterStatus, filterHorizon]);

    const hasFilters = filterVehicle || filterStatus || filterHorizon;
    const clearFilters = () => { setFilterVehicle(""); setFilterStatus(""); setFilterHorizon(""); };

    return (
        <div className="p-6 lg:p-8 animate-fade-in">
            <PageHeader
                eyebrow="PREVENTIVE MAINTENANCE"
                title="PM Schedules"
                subtitle="Auto-generate work orders based on km / time intervals."
                action={<NewButton onClick={() => { setForm(empty); setEditing(null); setOpen(true); }} label="Add schedule" testid="add-pm-btn" />}
            />

            {/* Filters */}
            <Surface className="px-4 py-3 mb-4 flex items-center gap-3 flex-wrap">
                <div className="min-w-[260px] flex-1 max-w-md">
                    <VehicleSearchSelect
                        vehicles={vehicles}
                        value={filterVehicle}
                        onChange={setFilterVehicle}
                        placeholder="Filter by vehicle reg #…"
                        allowClear
                        testid="pm-filter-vehicle"
                    />
                </div>
                <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-3 py-2 text-sm border border-fleet-border rounded-md bg-white focus:outline-none focus:border-fleet-ink"
                    data-testid="pm-filter-status"
                >
                    <option value="">All statuses</option>
                    <option value="due">Due</option>
                    <option value="near">Near due</option>
                    <option value="ok">OK</option>
                </select>
                <div className="inline-flex rounded-md border border-fleet-border bg-white text-xs overflow-hidden" data-testid="pm-filter-horizon">
                    <button
                        onClick={() => setFilterHorizon("")}
                        className={`px-2.5 py-1.5 ${filterHorizon === "" ? "bg-fleet-ink text-white" : "text-fleet-inkSoft hover:text-fleet-ink"}`}
                    >Any time</button>
                    <button
                        onClick={() => setFilterHorizon("15")}
                        className={`px-2.5 py-1.5 border-l border-fleet-border ${filterHorizon === "15" ? "bg-fleet-ink text-white" : "text-fleet-inkSoft hover:text-fleet-ink"}`}
                        data-testid="pm-filter-15"
                    >Within 15 days</button>
                    <button
                        onClick={() => setFilterHorizon("30")}
                        className={`px-2.5 py-1.5 border-l border-fleet-border ${filterHorizon === "30" ? "bg-fleet-ink text-white" : "text-fleet-inkSoft hover:text-fleet-ink"}`}
                        data-testid="pm-filter-30"
                    >Within 30 days</button>
                </div>
                {hasFilters && (
                    <button onClick={clearFilters} className="text-xs text-fleet-inkSoft hover:text-fleet-ink underline flex items-center gap-1" data-testid="pm-clear-filters">
                        <X size={12} /> Clear
                    </button>
                )}
                <span className="ml-auto text-xs text-fleet-inkSoft">{filtered.length} of {list.length}</span>
            </Surface>

            <Surface className="overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50"><tr className="text-left">
                        {["VEHICLE", "NAME", "EVERY KM", "EVERY DAYS", "LAST SVC", "STATUS", ""].map((h) => <th key={h} className="label-mono px-5 py-3 font-normal">{h}</th>)}
                    </tr></thead>
                    <tbody>
                        {filtered.map((p) => {
                            const veh = p._veh;
                            const due = p._status === "due";
                            const near = p._status === "near";
                            return (
                                <tr key={p.id} className="border-t border-fleet-border data-grid-row">
                                    <td className="px-5 py-3 font-mono">{veh?.reg_number || "—"}</td>
                                    <td className="px-5 py-3 font-medium">{p.name}</td>
                                    <td className="px-5 py-3 font-mono">{p.interval_km?.toLocaleString() || 0}</td>
                                    <td className="px-5 py-3 font-mono">{p.interval_days || 0}</td>
                                    <td className="px-5 py-3 font-mono">{(p.last_service_km || 0).toLocaleString()} km</td>
                                    <td className="px-5 py-3">{due ? <Badge variant="critical">DUE</Badge> : near ? <Badge variant="warning">NEAR</Badge> : <Badge variant="healthy">OK</Badge>}</td>
                                    <td className="px-5 py-3 text-right">
                                        <Button variant="secondary" size="sm" onClick={() => trigger(p)} data-testid={`trigger-pm-${p.id}`}>Generate WO</Button>
                                        <Button variant="ghost" size="sm" onClick={() => edit(p)} data-testid={`edit-pm-${p.id}`}>Edit</Button>
                                        <Button variant="ghost" size="sm" onClick={() => del(p.id)} data-testid={`del-pm-${p.id}`}>Del</Button>
                                    </td>
                                </tr>
                            );
                        })}
                        {filtered.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-sm text-fleet-inkSoft">{list.length === 0 ? "No schedules yet." : "No schedules match the current filters."}</td></tr>}
                    </tbody>
                </table>
            </Surface>

            <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit schedule" : "Add PM schedule"}
                footer={<><Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save}>Save</Button></>}>
                <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <VehicleSearchSelect
                            vehicles={vehicles}
                            value={form.vehicle_id}
                            onChange={(id) => setForm({ ...form, vehicle_id: id })}
                            label="Vehicle"
                            placeholder="Search by reg #…"
                            testid="pm-vehicle-search"
                        />
                    </div>
                    <Input label="Service name" className="col-span-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Engine oil change" />
                    <Input label="Every (km)" type="number" value={form.interval_km} onChange={(e) => setForm({ ...form, interval_km: e.target.value })} />
                    <Input label="Every (days)" type="number" value={form.interval_days} onChange={(e) => setForm({ ...form, interval_days: e.target.value })} />
                    <Input label="Last service km" className="col-span-2" type="number" value={form.last_service_km} onChange={(e) => setForm({ ...form, last_service_km: e.target.value })} />
                </div>
            </Modal>
        </div>
    );
}
